import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AiBusinessProfile } from "@gigflow/shared";
import { apiRequest } from "../api";
import { useAuth } from "../auth";
import { SiteRenderer, type PublicSiteData } from "../components/SiteRenderer";

type ChatItem = { role: "user" | "assistant"; content: string };

function previewFromProfile(profile: AiBusinessProfile): PublicSiteData {
  return {
    business: {
      id: "preview",
      name: profile.businessName,
      slug: "preview",
      industry: profile.industry,
      description: profile.description,
      phoneNumber: profile.phoneNumber,
      email: profile.email || null,
      primaryColor: profile.primaryColor,
      secondaryColor: profile.secondaryColor,
      heroHeadline: profile.heroHeadline,
      callToAction: profile.callToAction,
      aboutContent: profile.aboutContent,
      city: profile.address.city,
      region: profile.address.region,
      country: profile.address.country,
      currencyCode: profile.address.country === "GB" ? "GBP" : "USD",
    },
    website: {
      themeId: profile.themeId,
      layoutFamily: profile.layoutFamily,
      status: "DRAFT",
      sectionOrder: profile.sectionOrder ?? [
        "header",
        "hero",
        "about",
        "services",
        "pricing",
        "serviceAreas",
        "faq",
        "bookingCta",
        "contact",
        "footer",
      ],
      enabledSections: profile.enabledSections ?? [
        "header",
        "hero",
        "about",
        "services",
        "pricing",
        "serviceAreas",
        "faq",
        "bookingCta",
        "contact",
        "footer",
      ],
      seoTitle: profile.seoTitle,
      seoDescription: profile.seoDescription,
      sections: [
        { key: "header", content: { showNav: true } },
        {
          key: "hero",
          content: {
            headline: profile.heroHeadline,
            subheadline: profile.description.slice(0, 240),
            cta: profile.callToAction,
          },
        },
        { key: "about", content: { body: profile.aboutContent } },
        {
          key: "services",
          content: { intro: profile.pricingModel || "Our services" },
        },
        { key: "pricing", content: { intro: "Clear pricing" } },
        {
          key: "serviceAreas",
          content: { intro: "Where we work", areas: profile.serviceAreas },
        },
        { key: "faq", content: { items: profile.faqs } },
        {
          key: "bookingCta",
          content: {
            headline: "Ready when you are",
            cta: profile.callToAction,
          },
        },
        {
          key: "contact",
          content: {
            phone: profile.phoneNumber,
            email: profile.email,
          },
        },
        { key: "footer", content: { showPolicies: true } },
      ],
    },
    services: profile.services.map((s, index) => ({
      id: `svc-${index}`,
      name: s.name,
      description: s.description,
      pricingType: s.pricingType,
      priceCents: s.priceCents,
      durationMinutes: s.durationMinutes,
    })),
    serviceAreas: profile.serviceAreas.map((name, index) => ({
      id: `area-${index}`,
      name,
    })),
    policies: profile.cancellationPolicy
      ? [
          {
            key: "cancellation",
            title: "Cancellation policy",
            body: profile.cancellationPolicy,
          },
        ]
      : [],
    reviews: [],
  };
}

const STARTERS = [
  "I run a premium residential cleaning business in London with weekly, deep and move-out cleaning. Elegant site, online booking, customer portal.",
  "We are Bright Home Cleaning in New Haven, CT. Regular, deep and move-out cleaning for New Haven, Hamden and North Haven.",
  "Green View Landscaping — lawn care, mulching and seasonal cleanups. Quote-first for larger projects.",
];

export function OnboardingPage() {
  const { businessId, refreshBusinesses } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [chat, setChat] = useState<ChatItem[]>([]);
  const [profile, setProfile] = useState<AiBusinessProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const preview = useMemo(
    () => (profile ? previewFromProfile(profile) : null),
    [profile],
  );

  async function send(userText: string) {
    if (!businessId || !userText.trim()) return;
    setPending(true);
    setError(null);
    setChat((prev) => [...prev, { role: "user", content: userText.trim() }]);
    setMessage("");
    try {
      const result = await apiRequest<{
        conversationId: string;
        message: { content: string };
        profile: AiBusinessProfile;
      }>(`/businesses/${businessId}/ai/onboarding/chat`, {
        method: "POST",
        body: JSON.stringify({ conversationId, message: userText.trim() }),
      });
      setConversationId(result.conversationId);
      setProfile(result.profile);
      const followUps = result.profile.followUpQuestions?.length
        ? `\n\nNext: ${result.profile.followUpQuestions.slice(0, 2).join(" · ")}`
        : "";
      setChat((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `${result.profile.generationSummary || result.message.content}${followUps}`,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Generation failed");
    } finally {
      setPending(false);
    }
  }

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    await send(message);
  }

  async function approve() {
    if (!businessId || !conversationId || !profile) return;
    setPending(true);
    setError(null);
    try {
      await apiRequest(`/businesses/${businessId}/ai/onboarding/approve`, {
        method: "POST",
        body: JSON.stringify({ conversationId, profile }),
      });
      await refreshBusinesses();
      navigate("/app/website");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Approve failed");
    } finally {
      setPending(false);
    }
  }

  if (!businessId) {
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );
  }

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">AI website creator</p>
          <h1>Describe the business. Watch the site take shape.</h1>
        </div>
      </header>
      <section className="onboarding-stage">
        <div className="onboarding-chat">
          <div className="chat-log">
            {chat.length === 0 ? (
              <div className="notice">
                <p>
                  Start with a natural description — services, city, tone,
                  booking needs.
                </p>
                <div className="starter-chips">
                  {STARTERS.map((starter) => (
                    <button
                      key={starter}
                      type="button"
                      className="chip"
                      disabled={pending}
                      onClick={() => void send(starter)}
                    >
                      {starter.slice(0, 72)}…
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {chat.map((item, index) => (
              <div key={index} className={`chat-bubble ${item.role}`}>
                {item.content}
              </div>
            ))}
          </div>
          <form className="form-grid" onSubmit={sendMessage}>
            <label>
              Tell the AI what to create or change
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Make it more premium, add office cleaning, use UK postcodes…"
              />
            </label>
            <div className="cta-row">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={pending}
              >
                {pending ? "Generating…" : "Generate / update"}
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                disabled={!profile || pending}
                onClick={approve}
              >
                Approve & continue
              </button>
            </div>
          </form>
          {profile ? (
            <div className="generation-meta">
              <span>{profile.themeId.replaceAll("_", " ")}</span>
              <span>{profile.layoutFamily.replaceAll("_", " ")}</span>
              <span>{profile.contentTone}</span>
              <span>{profile.address.country}</span>
            </div>
          ) : null}
          {error ? <p className="notice error">{error}</p> : null}
        </div>
        <div className="onboarding-preview">
          <div className="preview-toolbar">
            <strong>Live website preview</strong>
            <span className="notice">
              {profile
                ? "Updates as you chat"
                : "Waiting for first description"}
            </span>
          </div>
          <div className="preview-frame">
            {preview ? (
              <SiteRenderer site={preview} slug="preview" />
            ) : (
              <div className="preview-empty">
                Your homepage, services, and booking call-to-action will appear
                here.
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
